import { createContext, useReducer, useEffect } from 'react';
import { supabase } from '../supabase/config';

export const AuthContext = createContext();

const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN':
      return { ...state, user: action.payload };
    case 'LOGOUT':
      return { ...state, user: null };
    case 'AUTH_IS_READY':
      return { ...state, user: action.payload, authIsReady: true };
    default:
      return state;
  }
};

export const AuthContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, {
    user: null,
    authIsReady: false
  });

  useEffect(() => {
    // Check for existing session on page load
    const getInitialSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      dispatch({ 
        type: 'AUTH_IS_READY', 
        payload: session?.user || null 
      });
    };
    
    getInitialSession();

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        dispatch({ 
          type: 'AUTH_IS_READY', 
          payload: session?.user || null 
        });
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const signup = async (email, password, displayName) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            display_name: displayName
          }
        }
      });
      
      if (error) throw error;
      
      // The user record in the 'users' table will be created automatically by a database trigger
      
      dispatch({ type: 'LOGIN', payload: data.user });
      return data.user;
    } catch (error) {
      throw error;
    }
  };

  const login = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) throw error;
      
      dispatch({ type: 'LOGIN', payload: data.user });
      return data.user;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      dispatch({ type: 'LOGOUT' });
    } catch (error) {
      throw error;
    }
  };

  const updateProfile = async (updates) => {
    try {
      // Update user metadata
      const { data, error: authError } = await supabase.auth.updateUser({
        data: { 
          display_name: updates.display_name,
          avatar_url: updates.avatar_url
        }
      });
      
      if (authError) throw authError;
      
      // Get the latest user data
      const { data: currentSession } = await supabase.auth.getSession();
      const currentUser = currentSession?.session?.user;
      
      // Update the user record in the database
      const { error: dbError } = await supabase
        .from('users')
        .upsert({
          id: currentUser.id,
          email: currentUser.email,
          display_name: updates.display_name,
          bio: updates.bio,
          location: updates.location,
          avatar_url: updates.avatar_url,
          updated_at: new Date().toISOString()
        });
      
      if (dbError) {
        console.error('Error updating user in database:', dbError);
      }
      
      // Get the updated user profile from the database
      const { data: updatedProfile, error: profileError } = await supabase
        .from('users')
        .select('*')
        .eq('id', currentUser.id)
        .single();
        
      if (profileError) {
        console.error('Error fetching updated profile:', profileError);
      }
      
      // Merge auth user with profile data
      const mergedUser = {
        ...currentUser,
        ...data.user,
        profile: updatedProfile || {}
      };
      
      // Update the user in the context
      dispatch({ type: 'LOGIN', payload: mergedUser });
      
      return mergedUser;
    } catch (error) {
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ ...state, login, logout, signup, updateProfile }}>
      {children}
    </AuthContext.Provider>
  );
}; 